import React, { useState } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import AdminProviderDetailsDialog from "./AdminProviderDetailsDialog";

const STATUS_OPTIONS = [
    { label: "Draft", value: "Draft" },
    { label: "Sent", value: "Sent" },
    { label: "Partially Paid", value: "Partial" },
    { label: "Paid", value: "Paid" },
    { label: "Processing", value: "Processing" },
];

function formatDate(d) {
    return d.toISOString().slice(0, 10); // "yyyy-MM-dd"
}

const AdminInvoices = () => {
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [search, setSearch] = useState("");
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [selectedProviderId, setSelectedProviderId] = useState(null);

    const buildBody = () => {
        const body = {};
        if (search && search.trim()) {
            body.search = search.trim();
        }
        if (selectedStatuses && selectedStatuses.length > 0) {
            body.statuses = selectedStatuses;
        }
        if (dateRange?.[0]) {
            body.invoiceDateStart = formatDate(dateRange[0]);
        }
        if (dateRange?.[1]) {
            body.invoiceDateEnd = formatDate(dateRange[1]);
        }
        return body;
    };

    const dateRangeKey = dateRange
        ? [dateRange[0]?.toISOString?.(), dateRange[1]?.toISOString?.()]
        : null;

    const { data, isLoading, isFetching, isError, refetch } = useQuery(
        ["AdminInvoicesReport", search, selectedStatuses, dateRangeKey],
        async () => {
            const result = await authApi.post(
                "admin/report/invoices-by-provider",
                buildBody()
            );
            return result.data;
        },
    );

    const rows = data?.rows ?? [];
    const grandTotal = data?.grandTotal ?? 0;

    const pageHeader = (
        <PageHeading pageTitle="Admin - Invoices Report">
            <div className="block"></div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const totalAmountBodyTemplate = (rowData) => {
        return (
            <>
                {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(rowData.totalAmount)}
            </>
        );
    };

    const clearFilters = () => {
        setSearch("");
        setSelectedStatuses([]);
        setDateRange(null);
    };

    const onProviderIdClick = (e, rowData) => {
        e.preventDefault();
        setSelectedProviderId(rowData.providerId);
        setShowDetailsDialog(true);
    };

    const providerIdBodyTemplate = (rowData) => (
        <a href="#" onClick={(e) => onProviderIdClick(e, rowData)}>
            {rowData.providerId}
        </a>
    );

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full flex-wrap align-items-center gap-2">
                        <div className="font-bold mr-2">Filter By</div>
                        <InputText
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Provider ID..."
                            className="max-w-20rem"
                        />
                        <div className="font-bold mr-2 ml-2">Status</div>
                        <MultiSelect
                            value={selectedStatuses}
                            onChange={(e) => setSelectedStatuses(e.value)}
                            options={STATUS_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="All statuses"
                            maxSelectedLabels={3}
                            className="w-full md:w-20rem"
                        />
                        <div className="font-bold mr-2 ml-2">Invoice Date Range</div>
                        <Calendar
                            value={dateRange}
                            onChange={(e) => setDateRange(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            dateFormat="yy-mm-dd"
                            placeholder="Select date range"
                            className="w-full md:w-auto"
                        />
                        {(search.trim() || selectedStatuses.length > 0 || dateRange) && (
                            <Button onClick={clearFilters} className="p-button-text" icon="pi pi-times" tooltip="Clear Filter" />
                        )}
                    </div>
                    <DataTable
                        value={rows}
                        dataKey="providerId"
                        className="datatable-responsive"
                        emptyMessage="No invoices match the filter criteria."
                        loading={isLoading}
                        sortField="totalAmount"
                        sortOrder={-1}
                    >
                        <Column field="providerId" header="Provider ID" body={providerIdBodyTemplate} />
                        <Column field="invoiceCount" header="Invoice Count" />
                        <Column
                            field="totalAmount"
                            header="Total Amount"
                            body={totalAmountBodyTemplate}
                            className="text-right"
                            headerClassName="right-aligned-header"
                            footer={
                                rows.length > 0
                                    ? new Intl.NumberFormat("en-US", {
                                          style: "currency",
                                          currency: "USD",
                                      }).format(grandTotal)
                                    : null
                            }
                            footerStyle={{ fontWeight: "bold" }}
                            sortable
                        />
                    </DataTable>
                </div>
                <AdminProviderDetailsDialog
                    visible={showDetailsDialog}
                    onHide={() => {
                        setShowDetailsDialog(false);
                        setSelectedProviderId(null);
                    }}
                    providerId={selectedProviderId}
                />
            </div>
        </div>
    );
};

export default AdminInvoices;
